import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Check, ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

const PLANS = [
  {
    id: "essential",
    name: "Monthly Checkup",
    price: 19,
    cadence: "Monthly review — no continuous monitoring",
    features: ["Monthly operations checkup", "Monthly website review", "Donation page check", "Contact form check", "SSL & domain checks", "Monthly health report", "Up to 5 reminders"],
    highlight: false,
  },
  {
    id: "complete",
    name: "Protection",
    price: 39,
    cadence: "Continuous protection — hourly checks",
    features: ["Every donation form on your site — checked every hour", "Everything in Monthly Checkup", "Whole-site coverage (up to 150 pages)", "Alerts within the hour", "Public verification badge", "Unlimited team members", "Priority support"],
    highlight: true,
  },
];

export default function GetStarted() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    if (user && user.subscription_status === "active") navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const startTrial = async (plan_id) => {
    setLoading(plan_id);
    try {
      const { data } = await api.post("/checkout/session", { plan_id, origin_url: window.location.origin });
      if (data.plan_change) {
        toast.success(data.message || "You're now on the new plan.");
        setTimeout(() => navigate("/dashboard"), 700);
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-charcoal-50" data-testid="get-started-page">
      <header className="border-b border-black/5 bg-white/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-charcoal-900 grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="font-semibold tracking-tight text-charcoal-900">Mission Watch</span>
          </Link>
          <span className="text-sm text-charcoal-500">{user?.email}</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-14">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-100 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-xs font-mono font-medium text-emerald-700">7-Day Free Trial</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter text-charcoal-900">
            Start protecting your organization today
          </h1>
          <p className="mt-4 text-base text-charcoal-500 leading-relaxed">
            Pick your plan and start your <span className="text-charcoal-800 font-medium">7-day free trial</span>. Run
            unlimited Mission Watch Scores during your trial. You won't be charged until your trial ends —
            cancel anytime before your first billing date.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
          {PLANS.map((p) => (
            <div key={p.id}
              className={`relative rounded-2xl p-7 border transition-shadow ${p.highlight ? "bg-charcoal-900 text-white border-charcoal-900 shadow-premium" : "bg-white border-black/5 shadow-soft"}`}
              data-testid={`plan-card-${p.id}`}>
              {p.highlight && (
                <span className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-emerald-500 text-white text-[11px] font-medium">Most popular</span>
              )}
              <p className={`text-sm font-medium ${p.highlight ? "text-white/70" : "text-charcoal-500"}`}>{p.name}</p>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-4xl font-semibold tracking-tighter">${p.price}</span>
                <span className={`text-sm ${p.highlight ? "text-white/50" : "text-charcoal-400"}`}>/month</span>
              </div>
              <p className={`mt-1 text-xs font-mono ${p.highlight ? "text-emerald-400" : "text-brand"}`}>{p.cadence}</p>
              <p className={`mt-3 text-xs ${p.highlight ? "text-white/50" : "text-charcoal-400"}`}>Free for 7 days, then ${p.price}/mo</p>

              <ul className="mt-5 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2.5 text-sm ${p.highlight ? "text-white/90" : "text-charcoal-700"}`}>
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.highlight ? "text-emerald-400" : "text-emerald-500"}`} />
                    {f}
                  </li>
                ))}
              </ul>

              <button onClick={() => startTrial(p.id)} disabled={loading === p.id}
                className={`mt-7 w-full h-12 rounded-full font-medium text-sm inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60 ${p.highlight ? "bg-brand text-white hover:bg-brand-hover" : "bg-charcoal-900 text-white hover:bg-charcoal-800"}`}
                data-testid={`start-trial-${p.id}`}>
                {loading === p.id ? "Starting…" : "Start My 7-Day Free Trial"}
                {loading !== p.id && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-xs text-charcoal-400">
          Secure payment by Stripe · Cancel anytime before your first billing date · No charge during trial
        </p>
      </main>
    </div>
  );
}
